import { useMemo, useState } from 'react';

import { toastError } from '@shared/plugIn/toast';
import { useAuth } from '@shared/state/hooks/useAuth';

import { CsMemoAdapter, CsMemoRecord } from './csMemoAdapter';

const fmtMemoTime = (date: Date | string | undefined) => {
  if (!date) return '';
  const d = new Date(date);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())} ${p(
    d.getHours(),
  )}:${p(d.getMinutes())}`;
};

const errMessage = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
  (e instanceof Error ? e.message : fallback);

export interface CsMemoPanelBaseProps<TTarget extends object> {
  adapter: CsMemoAdapter<TTarget>;
  target: TTarget;
  /**
   * 이미 조회해 둔 메모. 주면 자체 조회하지 않는다.
   * (상세 화면은 상위 축으로 한 번만 조회해 단위별로 갈라 쓴다)
   */
  memos?: CsMemoRecord[];
  isLoading?: boolean;
  /** 백엔드 미배포(404) — 입력창을 막고 안내만 띄운다 */
  unavailable?: boolean;
  /** 통합 타임라인에서 하위 단위 메모에 붙일 라벨 (대상 _id → 표시명) */
  subLabelMap?: Map<string, string>;
  placeholder?: string;
  listMaxHeightClassName?: string;
  autoFocus?: boolean;
  /**
   * 읽기 전용 — 입력창과 삭제 버튼을 아예 감춘다.
   * 남이 남긴 응대 이력을 참고만 해야 하는 역할(공급사·배송관리자)용.
   */
  readOnly?: boolean;
}

/**
 * CS 응대 메모 타임라인 + 입력 한 칸 (답례품·화환 공용).
 * 수정은 없고(정정은 새 메모), 삭제는 본인 메모 + Admin은 전체.
 * 스태프 전용 화면에서만 사용할 것 — 구매자·수령자 화면 금지.
 */
export function CsMemoPanelBase<TTarget extends object>({
  adapter,
  target,
  memos,
  isLoading,
  unavailable,
  subLabelMap,
  placeholder,
  listMaxHeightClassName = 'max-h-64',
  autoFocus,
  readOnly,
}: CsMemoPanelBaseProps<TTarget>) {
  const { isAdmin, userProfile } = useAuth();
  const admin = isAdmin();
  const myId = userProfile?._id;

  const [body, setBody] = useState('');
  const [adminOnly, setAdminOnly] = useState(false);

  // memos를 주입받지 않은 경우에만 자체 조회
  const selfQuery = adapter.useList(target, memos === undefined);
  const createMemo = adapter.useCreate();
  const removeMemo = adapter.useDelete();

  const loading = (isLoading ?? false) || (memos === undefined && selfQuery.isLoading);
  const notDeployed =
    (unavailable ?? false) || (memos === undefined && selfQuery.data === null);
  const noTarget = !adapter.hasTarget(target);

  // 로그처럼 읽히도록 오래된 것부터
  const selfData = selfQuery.data;
  const sorted = useMemo(
    () =>
      [...(memos ?? selfData ?? [])].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      ),
    [memos, selfData],
  );

  const handleSave = async () => {
    const trimmed = body.trim();
    if (!trimmed || createMemo.isLoading || noTarget) return;
    try {
      await createMemo.mutateAsync({
        ...target,
        body: trimmed,
        ...(admin && adminOnly ? { adminOnly: true } : {}),
      });
      setBody('');
      setAdminOnly(false);
    } catch (e) {
      toastError(errMessage(e, '메모 저장에 실패했습니다.'));
    }
  };

  // 삭제 권한 — 본인 메모, Admin은 전체
  const canDelete = (memo: CsMemoRecord) => admin || (!!myId && memo.by === myId);

  const handleDelete = async (memo: CsMemoRecord) => {
    if (removeMemo.isLoading) return;
    if (!window.confirm('이 메모를 삭제할까요?')) return;
    try {
      await removeMemo.mutateAsync({ memo });
    } catch (e) {
      if (adapter.isDeleteUnsupported(e)) {
        toastError('삭제 기능이 아직 서버에 반영되지 않았습니다.');
        return;
      }
      toastError(errMessage(e, '메모 삭제에 실패했습니다.'));
    }
  };

  const inputDisabled = notDeployed || noTarget;

  return (
    <div className="flex min-h-0 flex-col gap-2">
      {/* 타임라인 */}
      <div
        className={`min-h-[80px] overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-2 ${listMaxHeightClassName}`}
      >
        {loading ? (
          <p className="py-4 text-center text-xs text-gray-400">불러오는 중...</p>
        ) : notDeployed ? (
          <p className="py-4 text-center text-xs text-gray-400">서버 미반영</p>
        ) : sorted.length === 0 ? (
          <p className="py-4 text-center text-xs text-gray-400">메모 없음</p>
        ) : (
          <ul className="space-y-1.5">
            {sorted.map((memo) => {
              const badge = adapter.roleBadge(memo.byRole);
              const subId = memo.giftCoupon ?? memo.giftDelivery;
              const subLabel = subId ? subLabelMap?.get(subId) : undefined;
              return (
                <li
                  key={memo._id}
                  className={`rounded-md border px-2.5 py-1.5 ${
                    memo.adminOnly
                      ? 'border-amber-200 bg-amber-50'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span
                      className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${badge.className}`}
                    >
                      {badge.label}
                    </span>
                    <span className="text-[11px] font-medium text-gray-700">
                      {memo.byName}
                    </span>
                    <span className="text-[11px] text-gray-400">
                      {fmtMemoTime(memo.createdAt)}
                    </span>
                    {memo.adminOnly && (
                      <span className="inline-flex rounded-full bg-amber-200 px-1.5 py-0.5 text-[10px] font-semibold leading-tight text-amber-800">
                        관리자 전용
                      </span>
                    )}
                    {subLabel && (
                      <span className="inline-flex rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium leading-tight text-gray-600">
                        {subLabel}
                      </span>
                    )}
                    {!readOnly && canDelete(memo) && (
                      <button
                        type="button"
                        onClick={() => void handleDelete(memo)}
                        disabled={removeMemo.isLoading}
                        title="메모 삭제"
                        className="ml-auto text-[11px] text-gray-300 hover:text-red-500 disabled:opacity-50"
                      >
                        삭제
                      </button>
                    )}
                  </div>
                  <p className="mt-1 whitespace-pre-wrap break-words text-xs leading-relaxed text-gray-800">
                    {memo.body}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* 입력 — 읽기 전용이면 통째로 감춘다 */}
      {readOnly ? null : (
        <div className="space-y-1.5">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') void handleSave();
            }}
            autoFocus={autoFocus}
            disabled={inputDisabled}
            rows={3}
            placeholder={placeholder ?? '응대 내용 (Ctrl+Enter 저장)'}
            className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-xs focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400 disabled:bg-gray-100"
          />
          <div className="flex items-center justify-between gap-2">
            {admin ? (
              <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-gray-600">
                <input
                  type="checkbox"
                  checked={adminOnly}
                  onChange={(e) => setAdminOnly(e.target.checked)}
                  disabled={inputDisabled}
                  className="h-3.5 w-3.5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                관리자 전용
              </label>
            ) : (
              <span className="text-[11px] text-gray-400">수정 불가 · 삭제는 가능</span>
            )}
            <div className="flex items-center gap-2">
              {!!body && (
                <button
                  type="button"
                  onClick={() => setBody('')}
                  disabled={createMemo.isLoading}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                >
                  지우기
                </button>
              )}
              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={!body.trim() || createMemo.isLoading || inputDisabled}
                className="rounded-lg bg-indigo-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {createMemo.isLoading ? '저장 중...' : '메모 남기기'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
